
import React from "react";
import { Message as UIMessage, MessageTone } from "@/types/message";
import { Conversation } from "@/types/conversation";
import { DBMessage } from "@/types/message";
import MessageList from "./messages/MessageList";
import ConversationLoading from "./conversation/ConversationLoading";
import ConversationActions from "./conversation/ConversationActions";
import FlightClaimLetterDisplay from "./conversation/FlightClaimLetterDisplay";
import { useConversationThread } from "./hooks/useConversationThread";
import type { ClarificationType } from "./ClarificationInput";

interface ConversationThreadProps {
  messages: UIMessage[];
  companyName: string;
  orderNumber: string;
  onPasteSellerResponse: (content: string) => void;
  onRegenerateAIResponse: (messageId: string, tone: MessageTone, customInstructions?: string) => Promise<UIMessage | null>;
  isAIGenerating: boolean;
  isRegenerating: boolean;
  regeneratingMessageId?: string;
  isPasting: boolean;
  isProcessingAI: boolean;
  userEmail?: string;
  sellerEmail?: string;
  lastAIMessage?: UIMessage | null;
  // New props for advanced features
  conversation?: Conversation;
  dbMessages?: DBMessage[];
  onClarificationSubmit?: (clarification: string, type: ClarificationType) => void;
  onGenerateFollowUp?: (type: string) => void;
  isClarificationSubmitting?: boolean;
}

const ConversationThread = ({
  messages,
  companyName,
  orderNumber,
  onPasteSellerResponse,
  onRegenerateAIResponse,
  isAIGenerating,
  isRegenerating,
  regeneratingMessageId,
  isPasting,
  isProcessingAI,
  userEmail = "user@example.com",
  sellerEmail = "support@company.com",
  lastAIMessage = null,
  conversation,
  dbMessages = [],
  onClarificationSubmit,
  onGenerateFollowUp,
  isClarificationSubmitting = false
}: ConversationThreadProps) => {
  // Use our extracted hook to handle the thread logic
  const {
    latestAIMessage,
    hasAIMessages,
    isAnyLoading,
    handleGenerateInitialResponse
  } = useConversationThread({
    messages,
    isRegenerating,
    isAIGenerating,
    isPasting,
    isProcessingAI,
    onPasteSellerResponse,
  });

  // Determine the appropriate header based on conversation type
  const getConversationHeader = () => {
    if (conversation?.request_type === "flight_compensation") {
      return `${companyName} Customer Service`; // e.g., "Lufthansa Customer Service"
    }
    return companyName; // Default merchant name
  };

  const conversationHeader = getConversationHeader();

  // For flight compensation claims, show the letter display instead of regular messages
  const isFlightClaim = conversation?.request_type === "flight_compensation";
  
  return (
    <div className="space-y-6">
      {isFlightClaim && conversation?.generated_letter ? (
        <FlightClaimLetterDisplay 
          generatedLetter={conversation.generated_letter}
          companyName={conversation.merchant}
          flightNumber={conversation.order_number}
        />
      ) : (
        <>
          <MessageList 
            messages={messages} 
            companyName={conversationHeader}
            onRegenerateAIResponse={onRegenerateAIResponse}
            isRegenerating={isRegenerating}
            regeneratingMessageId={regeneratingMessageId}
            userEmail={userEmail}
            sellerEmail={sellerEmail}
            orderNumber={orderNumber}
            isAIGenerating={isAIGenerating}
          />
          
          <ConversationLoading 
            isRegenerating={isRegenerating}
            isAIGenerating={isAIGenerating}
            isPasting={isPasting}
            isProcessingAI={isProcessingAI}
          />
          
          <ConversationActions
            isAnyLoading={isAnyLoading}
            hasAIMessages={hasAIMessages}
            messages={messages}
            onGenerateInitialResponse={handleGenerateInitialResponse}
            onPasteSellerResponse={onPasteSellerResponse}
            isPasting={isPasting}
            isProcessingAI={isProcessingAI}
            latestAIMessage={latestAIMessage || lastAIMessage}
            conversation={conversation}
            dbMessages={dbMessages}
            onClarificationSubmit={onClarificationSubmit}
            onGenerateFollowUp={onGenerateFollowUp}
            isClarificationSubmitting={isClarificationSubmitting}
          />
        </>
      )}
    </div>
  );
};

export default ConversationThread;
